import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplayIcon from "@mui/icons-material/Replay";
import { visuallyHidden } from "@mui/utils";
import PrintIcon from "@mui/icons-material/Print";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { useReactToPrint } from "react-to-print";
import { FilledButton } from "../../customComponents/styled/styledButtons";
import { StyledTextField } from "../../customComponents/styled/styledInputs";
import { Stack } from "@mui/material";
import { format } from "date-fns";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import UserAvatar from "../../common/UserAvatar";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis?.map((el) => el[0]);
}

function EnhancedTableHead(props) {
  const headCells = [
    {
      id: "photo",

      label: "Photo",
    },
    {
      id: "title",

      label: "Title",
    },
    {
      id: "name",
      label: "Name",
    },
    {
      id: "phone",
      label: "Phone",
    },
    {
      id: "status",
      label: "Status",
    },
    {
      id: "reset",
      label: "Reset",
    },
    {
      id: "delete",
      label: "Delete",
    },
  ];

  const { order, orderBy, onRequestSort } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  rows: PropTypes.array.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

const EnhancedTableToolbar = (props) => {
  const { searchQuery, onSearchChange, handlePrint } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center",
        gap: 1,

        "& .search": {
          margin: isMobile ? "0" : "0px 5px",
        },
      }}
    >
      <FilledButton onClick={handlePrint}>
        Print
        <PrintIcon />
      </FilledButton>
      <StyledTextField
        className="search"
        placeholder="search..."
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
      />
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  onSearchChange: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  handlePrint: PropTypes.func.isRequired,
};

export default function StaffTable({
  rows: initialRows,
  schoolInfo,
  pagination,
  page,
  perPage,
  searchQuery,
  onPageChange,
  onPerPageChange,
  onSearchChange,
  onToggleStatus,
  setSelectedStaff,
  setOpenEdit,
  setOpenWarningDialog,
  setSelectedStaffForReset,
  setOpenResetDialog,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("name");
  const componentToPrintRef = React.useRef();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handlePrint = useReactToPrint({
    content: () => componentToPrintRef.current,
  });

  const sortedRows = React.useMemo(() => {
    if (!initialRows?.length) return [];
    return stableSort(initialRows, getComparator(order, orderBy));
  }, [initialRows, order, orderBy]);


  return (
    <Box sx={{ width: "100%" }}>
      <EnhancedTableToolbar
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        handlePrint={handlePrint}
      />
      <div style={{ display: "none", padding: "8px" }}>
        {/* Table to print, containing all the table data without searching or sorting */}
        <Box
          ref={componentToPrintRef}
          p={3}
          mb={2}
          sx={{
            "& table": {
              width: "100%",
              "& .th": {
                paddingBottom: "5px",
                borderBottom: "1px solid #f1f2f3",
                textTransform: "uppercase",
              },
            },
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Typography>{format(new Date(), "MM/dd/yyyy, KK:mm a")}</Typography>
            <Typography>{schoolInfo?.name} - Schoola</Typography>
          </Stack>
          <Typography variant={"h5"} textAlign="center" my={2}>
            {schoolInfo?.name} - Schoola
          </Typography>
          <table>
            <tr>
              <td className="th">S/N</td>
              <td className="th">Title</td>
              <td className="th">Name</td>
              <td className="th">Phone</td>
            </tr>
            {initialRows?.map((row, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{row.title}</td>
                <td>
                  {row.first_name} {row.last_name}
                </td>
                <td>{row.phone}</td>
              </tr>
            ))}
          </table>
        </Box>
        {/* <TableContainer ref={componentToPrintRef}>
          <Table>
            <TableHead>
              <Stack direction="row" justifyContent="space-between">
                <Typography>
                  {format(new Date(), "MM/dd/yyyy, KK:mm a")}
                </Typography>
                <Typography>{schoolInfo?.name}</Typography>
              </Stack>
              <Typography variant="h4" sx={{ marginBottom: "10px" }}>
                {schoolInfo?.name}
              </Typography>
              <TableRow sx={{ borderBottom: "2px solid lightgrey" }}>
                <StyledTableCell>S/N</StyledTableCell>
                <StyledTableCell>title</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Phone</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows?.map((row, i) => (
                <StyledTableRow key={i}>
                  <StyledTableCell>{i + 1}</StyledTableCell>
                  <StyledTableCell>{row.title}</StyledTableCell>
                  <StyledTableCell>
                    {row.first_name} {row.last_name}
                  </StyledTableCell>
                  <StyledTableCell>{row.phone}</StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer> */}
      </div>

      {isMobile ? (
        <Box sx={{ p: 2 }}>
          {sortedRows?.map((row) => (
            <Box
              key={row.id}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1.5,
                mb: 1.5,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <UserAvatar
                  src={row.image}
                  name={`${row.first_name || ""} ${row.last_name || ""}`}
                  sx={{ width: 36, height: 36, fontSize: "0.875rem" }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ textTransform: "capitalize", color: "primary.main", fontWeight: 600 }}>
                    {row.first_name} {row.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {row.title || "-"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {row.phone || "-"}
                  </Typography>
                </Box>
                <Switch
                  size="small"
                  checked={row.user?.is_active ?? true}
                  onChange={() => onToggleStatus?.(row)}
                />
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <FilledButton
                  onClick={() => {
                    setSelectedStaff(row);
                    setOpenEdit(true);
                  }}
                >
                  Edit
                </FilledButton>
                <FilledButton
                  onClick={() => {
                    setSelectedStaffForReset(row);
                    setOpenResetDialog(true);
                  }}
                >
                  <ReplayIcon />
                </FilledButton>
                <FilledButton
                  onClick={() => {
                    setSelectedStaff(row);
                    setOpenWarningDialog(true);
                  }}
                >
                  <DeleteIcon />
                </FilledButton>
              </Stack>
            </Box>
          ))}
        </Box>
      ) : (
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {sortedRows
                ?.map((row) => {
                  return (
                    <StyledTableRow hover tabIndex={-1} key={row.id}>
                      <StyledTableCell
                        component="th"
                        scope="row"
                        sx={{ paddingLeft: "10px" }}
                      >
                        <UserAvatar
                          src={row.image}
                          name={`${row.first_name || ""} ${row.last_name || ""}`}
                          sx={{ width: 24, height: 24, fontSize: "0.55rem" }}
                        />
                      </StyledTableCell>
                      <StyledTableCell>{row.title}</StyledTableCell>
                      <StyledTableCell>
                        <Box
                          className="tableCellLink"
                          onClick={() => {
                            setSelectedStaff(row);
                            setOpenEdit(true);
                          }}
                          sx={{ textTransform: "capitalize" }}
                        >
                          {row.first_name} {row.last_name}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>{row.phone}</StyledTableCell>
                      <StyledTableCell>
                        <Switch
                          size="small"
                          checked={row.user?.is_active ?? true}
                          onChange={() => onToggleStatus?.(row)}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <ReplayIcon
                          className="tableCellLink"
                          sx={{
                            color: "primary.safeOnLight",
                          }}
                          onClick={() => {
                            setSelectedStaffForReset(row);
                            setOpenResetDialog(true);
                          }}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <DeleteIcon
                          className="tableCellLink"
                          onClick={() => {
                            setSelectedStaff(row);
                            setOpenWarningDialog(true);
                          }}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        rowsPerPageOptions={[10, 20, 50]}
        component="div"
        count={pagination?.total_items ?? (initialRows?.length ?? 0)}
        rowsPerPage={perPage}
        page={page - 1}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        onRowsPerPageChange={(e) => onPerPageChange(parseInt(e.target.value, 10))}
        sx={{ '.MuiTablePagination-toolbar': { flexWrap: 'wrap', minWidth: 0, px: 0 }, '.MuiTablePagination-spacer': { display: 'none' } }}
      />
    </Box>
  );
}
